import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import {
  ShoppingCart, Star, Clock, Search, Loader2, Plus, Trash2,
  CalendarIcon, Package, Briefcase, Video, BadgeCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERVICE_CATEGORIES = [
  { id: 'consultation', name: 'Consultation' },
  { id: 'documentation', name: 'Documentation' },
  { id: 'filing', name: 'Filing' },
  { id: 'review', name: 'Case Review' },
];

const MarketplacePage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [purchases, setPurchases] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');

  // Service creation state
  const [showCreateService, setShowCreateService] = useState(false);
  const [creatingService, setCreatingService] = useState(false);
  const [newService, setNewService] = useState({ title: '', description: '', price: '', category: 'consultation', duration_minutes: '' });

  // Meeting scheduling state
  const [schedulingMeeting, setSchedulingMeeting] = useState(null); // purchase object
  const [meetingDate, setMeetingDate] = useState(new Date());
  const [meetingNotes, setMeetingNotes] = useState('');
  const [submittingMeeting, setSubmittingMeeting] = useState(false);

  // Review state
  const [reviewingPurchase, setReviewingPurchase] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviews, setReviews] = useState({});  // service_id -> [reviews]

  const isProvider = user?.role === 'lawyer' || user?.role === 'broker' || user?.role === 'admin';

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketplace/services`, { headers: getAuthHeaders(), withCredentials: true });
      setServices(res.data);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketplace/cart`, { headers: getAuthHeaders(), withCredentials: true });
      setCart(res.data);
    } catch {}
  }, [getAuthHeaders]);

  const fetchPurchases = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketplace/purchases`, { headers: getAuthHeaders(), withCredentials: true });
      setPurchases(res.data);
    } catch {}
  }, [getAuthHeaders]);

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/meetings/`, { headers: getAuthHeaders(), withCredentials: true });
      setMeetings(res.data);
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchServices();
    fetchCart();
    fetchPurchases();
    fetchMeetings();
  }, [fetchServices, fetchCart, fetchPurchases, fetchMeetings]);

  const addToCart = async (serviceId) => {
    try {
      await axios.post(`${API}/marketplace/cart/add`, { service_id: serviceId, quantity: 1 }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Added to cart!');
      fetchCart();
    } catch { toast.error('Failed to add to cart'); }
  };

  const removeFromCart = async (serviceId) => {
    try {
      await axios.delete(`${API}/marketplace/cart/${serviceId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Removed from cart');
      fetchCart();
    } catch { toast.error('Failed to remove'); }
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) return;
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/marketplace/checkout`, { origin_url: window.location.origin }, { headers: getAuthHeaders(), withCredentials: true });
      window.location.href = res.data.checkout_url;
    } catch { toast.error('Failed to start checkout'); setCheckingOut(false); }
  };

  const handleCreateService = async () => {
    if (!newService.title.trim() || !newService.description.trim() || !newService.price) return;
    setCreatingService(true);
    try {
      await axios.post(`${API}/marketplace/services`, {
        ...newService,
        price: parseFloat(newService.price),
        duration_minutes: newService.duration_minutes ? parseInt(newService.duration_minutes) : null,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Service created!');
      setShowCreateService(false);
      setNewService({ title: '', description: '', price: '', category: 'consultation', duration_minutes: '' });
      fetchServices();
    } catch { toast.error('Failed to create service'); }
    finally { setCreatingService(false); }
  };

  const handleScheduleMeeting = async () => {
    if (!schedulingMeeting) return;
    setSubmittingMeeting(true);
    try {
      await axios.post(`${API}/meetings/`, {
        service_id: schedulingMeeting.service_id,
        provider_id: schedulingMeeting.service?.provider_id,
        scheduled_date: meetingDate.toISOString(),
        notes: meetingNotes || null,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Meeting scheduled!');
      setSchedulingMeeting(null);
      setMeetingNotes('');
      fetchMeetings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule'); }
    finally { setSubmittingMeeting(false); }
  };

  const handleSubmitReview = async () => {
    if (!reviewingPurchase || reviewRating < 1) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews/`, {
        service_id: reviewingPurchase.service_id,
        rating: reviewRating,
        comment: reviewComment || null,
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Review submitted!');
      setReviewingPurchase(null);
      setReviewRating(0);
      setReviewComment('');
      fetchServices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const fetchReviews = async (serviceId) => {
    try {
      const res = await axios.get(`${API}/reviews/${serviceId}`, { headers: getAuthHeaders(), withCredentials: true });
      setReviews(prev => ({ ...prev, [serviceId]: res.data }));
    } catch {}
  };

  const handleConfirmMeeting = async (meetingId, meetingLink = '') => {
    try {
      await axios.put(`${API}/meetings/${meetingId}/confirm`, { meeting_link: meetingLink }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Meeting confirmed!');
      fetchMeetings();
    } catch { toast.error('Failed to confirm'); }
  };

  const handleCancelMeeting = async (meetingId) => {
    try {
      await axios.put(`${API}/meetings/${meetingId}/cancel`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Meeting cancelled');
      fetchMeetings();
    } catch { toast.error('Failed to cancel'); }
  };

  const filteredServices = services.filter((s) => {
    const matchCat = selectedCategory === 'all' || s.category === selectedCategory;
    const matchSearch = s.title.toLowerCase().includes(searchQuery.toLowerCase()) || s.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const myServices = services.filter(s => s.provider_id === user?.user_id);

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="marketplace-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold">Services Marketplace</h1>
            <p className="text-muted-foreground mt-1">Professional immigration services from verified experts</p>
          </div>
          <div className="flex gap-2">
            {isProvider && (
              <Dialog open={showCreateService} onOpenChange={setShowCreateService}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="create-service-btn">
                    <Plus className="h-4 w-4 mr-2" /> Offer Service
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-serif text-xl">Create New Service</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Service Title</Label>
                      <Input placeholder="e.g., H-1B Visa Consultation" value={newService.title} onChange={e => setNewService(p => ({...p, title: e.target.value}))} className="input-default" data-testid="service-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea placeholder="Describe your service..." value={newService.description} onChange={e => setNewService(p => ({...p, description: e.target.value}))} className="input-default min-h-[80px]" data-testid="service-desc-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Price (USD)</Label>
                        <Input type="number" min="1" placeholder="99.00" value={newService.price} onChange={e => setNewService(p => ({...p, price: e.target.value}))} className="input-default" data-testid="service-price-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration (min)</Label>
                        <Input type="number" min="15" placeholder="60" value={newService.duration_minutes} onChange={e => setNewService(p => ({...p, duration_minutes: e.target.value}))} className="input-default" data-testid="service-duration-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={newService.category} onValueChange={v => setNewService(p => ({...p, category: v}))}>
                        <SelectTrigger className="input-default"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SERVICE_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleCreateService} disabled={creatingService || !newService.title.trim() || !newService.price} className="w-full btn-primary" data-testid="submit-service-btn">
                      {creatingService ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Service'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative" data-testid="cart-btn">
                  <ShoppingCart className="h-4 w-4 mr-2" /> Cart
                  {cart.items.length > 0 && <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center">{cart.items.length}</Badge>}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader><SheetTitle className="font-serif">Shopping Cart</SheetTitle></SheetHeader>
                <div className="mt-6 flex flex-col h-full">
                  {cart.items.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">Your cart is empty</p>
                  ) : (
                    <>
                      <div className="flex-1 space-y-4 overflow-y-auto">
                        {cart.items.map(item => (
                          <div key={item.service_id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.title}</p>
                              <p className="text-sm text-muted-foreground">${item.price.toFixed(2)}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => removeFromCart(item.service_id)} data-testid={`remove-cart-${item.service_id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-border pt-4 mt-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">Total</span>
                          <span className="font-semibold text-lg">${cart.total.toFixed(2)}</span>
                        </div>
                        <Button className="w-full btn-primary" onClick={handleCheckout} disabled={checkingOut} data-testid="checkout-btn">
                          {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Proceed to Checkout'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="browse" data-testid="tab-browse"><Search className="h-4 w-4 mr-1.5" /> Browse</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases"><Package className="h-4 w-4 mr-1.5" /> My Purchases</TabsTrigger>
            <TabsTrigger value="meetings" data-testid="tab-meetings"><Video className="h-4 w-4 mr-1.5" /> Meetings</TabsTrigger>
            {isProvider && <TabsTrigger value="my-services" data-testid="tab-my-services"><Briefcase className="h-4 w-4 mr-1.5" /> My Services</TabsTrigger>}
          </TabsList>

          {/* Browse Tab */}
          <TabsContent value="browse">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 input-default" data-testid="search-services-input" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant={selectedCategory === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory('all')}>All</Button>
                {SERVICE_CATEGORIES.map(cat => (
                  <Button key={cat.id} variant={selectedCategory === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setSelectedCategory(cat.id)}>{cat.name}</Button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : filteredServices.length === 0 ? (
              <Card className="card-default text-center py-12"><p className="text-muted-foreground">No services found.</p></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service, index) => (
                  <Card key={service.service_id} className="card-default overflow-hidden animate-slide-up" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`service-${service.service_id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <Badge variant="secondary" className="mb-2">{SERVICE_CATEGORIES.find(c => c.id === service.category)?.name || service.category}</Badge>
                        <div className="flex items-center gap-1"><Star className="h-4 w-4 fill-primary text-primary" /><span className="text-sm">{service.rating?.toFixed(1) || '5.0'}</span></div>
                      </div>
                      <CardTitle className="text-lg">{service.title}</CardTitle>
                      <CardDescription className="line-clamp-2">{service.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8"><AvatarFallback>{service.provider_name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                        <div>
                          <div className="flex items-center gap-1">
                            <p className="text-sm font-medium">{service.provider_name}</p>
                            {service.provider_verified && <BadgeCheck className="h-3.5 w-3.5 text-blue-500" />}
                          </div>
                          <p className="text-xs text-muted-foreground capitalize">{service.provider_role}</p>
                        </div>
                      </div>
                      {service.duration_minutes && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4" />{service.duration_minutes} minutes</div>
                      )}
                      <Separator />
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-serif font-bold">${service.price.toFixed(2)}</span>
                        <Button className="btn-primary" onClick={() => addToCart(service.service_id)} data-testid={`add-to-cart-${service.service_id}`}>
                          <Plus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases">
            {purchases.length === 0 ? (
              <Card className="card-default text-center py-12">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No purchases yet. Browse services to get started.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {purchases.map(purchase => (
                  <Card key={purchase.purchase_id} className="card-default" data-testid={`purchase-${purchase.purchase_id}`}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Briefcase className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{purchase.service?.title || 'Service'}</h3>
                            <p className="text-sm text-muted-foreground">by {purchase.service?.provider_name}</p>
                            <p className="text-xs text-muted-foreground mt-1">Purchased {new Date(purchase.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-lg font-serif font-bold">${purchase.service?.price?.toFixed(2)}</span>
                          <Button size="sm" className="btn-primary" onClick={() => { setSchedulingMeeting(purchase); setMeetingDate(new Date()); setMeetingNotes(''); }} data-testid={`schedule-meeting-${purchase.purchase_id}`}>
                            <CalendarIcon className="h-4 w-4 mr-1.5" /> Meeting
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setReviewingPurchase(purchase); setReviewRating(0); setReviewComment(''); }} data-testid={`review-btn-${purchase.purchase_id}`}>
                            <Star className="h-4 w-4 mr-1.5" /> Review
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Meetings Tab */}
          <TabsContent value="meetings">
            {meetings.length === 0 ? (
              <Card className="card-default text-center py-12">
                <Video className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No meetings scheduled yet.</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {meetings.map(meeting => (
                  <Card key={meeting.meeting_id} className="card-default" data-testid={`meeting-${meeting.meeting_id}`}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            meeting.status === 'confirmed' ? 'bg-green-500/10' : meeting.status === 'cancelled' ? 'bg-destructive/10' : 'bg-amber-500/10'
                          }`}>
                            <Video className={`h-6 w-6 ${
                              meeting.status === 'confirmed' ? 'text-green-500' : meeting.status === 'cancelled' ? 'text-destructive' : 'text-amber-500'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{meeting.service_title}</h3>
                            <p className="text-sm text-muted-foreground">
                              with {meeting.provider_id === user?.user_id ? meeting.user_name : meeting.provider_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <CalendarIcon className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">{format(new Date(meeting.scheduled_date), 'PPP p')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={meeting.status === 'confirmed' ? 'default' : meeting.status === 'cancelled' ? 'destructive' : 'secondary'}>
                            {meeting.status}
                          </Badge>
                          {meeting.meeting_link && (
                            <Button size="sm" asChild><a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer">Join</a></Button>
                          )}
                          {meeting.status === 'pending' && meeting.provider_id === user?.user_id && (
                            <>
                              <Button size="sm" variant="default" onClick={() => {
                                const link = prompt('Meeting link (Zoom/Google Meet):');
                                if (link !== null) handleConfirmMeeting(meeting.meeting_id, link);
                              }} data-testid={`confirm-meeting-${meeting.meeting_id}`}>Confirm</Button>
                              <Button size="sm" variant="destructive" onClick={() => handleCancelMeeting(meeting.meeting_id)} data-testid={`cancel-meeting-${meeting.meeting_id}`}>Decline</Button>
                            </>
                          )}
                          {meeting.status === 'pending' && meeting.user_id === user?.user_id && (
                            <Button size="sm" variant="destructive" onClick={() => handleCancelMeeting(meeting.meeting_id)}>Cancel</Button>
                          )}
                        </div>
                      </div>
                      {meeting.notes && <p className="text-sm text-muted-foreground mt-3 pl-16">{meeting.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Services Tab (Providers) */}
          {isProvider && (
            <TabsContent value="my-services">
              {myServices.length === 0 ? (
                <Card className="card-default text-center py-12">
                  <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-4">You haven't created any services yet.</p>
                  <Button className="btn-primary" onClick={() => setShowCreateService(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Create Your First Service
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myServices.map(service => (
                    <Card key={service.service_id} className="card-default" data-testid={`my-service-${service.service_id}`}>
                      <CardHeader>
                        <Badge variant="secondary" className="w-fit mb-2">{SERVICE_CATEGORIES.find(c => c.id === service.category)?.name || service.category}</Badge>
                        <CardTitle className="text-lg">{service.title}</CardTitle>
                        <CardDescription className="line-clamp-2">{service.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-serif font-bold">${service.price.toFixed(2)}</span>
                          <div className="flex items-center gap-1"><Star className="h-4 w-4 fill-primary text-primary" /><span className="text-sm">{service.rating?.toFixed(1) || '5.0'}</span></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Meeting Scheduling Dialog */}
        <Dialog open={!!schedulingMeeting} onOpenChange={() => setSchedulingMeeting(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-xl">Schedule Meeting</DialogTitle></DialogHeader>
            {schedulingMeeting && (
              <div className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="font-medium">{schedulingMeeting.service?.title}</p>
                  <p className="text-sm text-muted-foreground">with {schedulingMeeting.service?.provider_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(meetingDate, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={meetingDate} onSelect={d => setMeetingDate(d || new Date())} initialFocus disabled={d => d < new Date()} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea placeholder="Any specific topics you'd like to discuss..." value={meetingNotes} onChange={e => setMeetingNotes(e.target.value)} className="input-default min-h-[80px]" data-testid="meeting-notes-input" />
                </div>
                <Button onClick={handleScheduleMeeting} disabled={submittingMeeting} className="w-full btn-primary" data-testid="submit-meeting-btn">
                  {submittingMeeting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Schedule Meeting'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Review Dialog */}
        <Dialog open={!!reviewingPurchase} onOpenChange={() => setReviewingPurchase(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-xl">Write a Review</DialogTitle></DialogHeader>
            {reviewingPurchase && (
              <div className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="font-medium">{reviewingPurchase.service?.title}</p>
                  <p className="text-sm text-muted-foreground">by {reviewingPurchase.service?.provider_name}</p>
                </div>
                <div className="space-y-2">
                  <Label>Rating</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button key={star} onClick={() => setReviewRating(star)} className="p-1 transition-transform hover:scale-110" data-testid={`star-${star}`}>
                        <Star className={`h-6 w-6 ${reviewRating >= star ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comment (optional)</Label>
                  <Textarea placeholder="Share your experience..." value={reviewComment} onChange={e => setReviewComment(e.target.value)} className="input-default min-h-[80px]" data-testid="review-comment-input" />
                </div>
                <Button onClick={handleSubmitReview} disabled={submittingReview || reviewRating < 1} className="w-full btn-primary" data-testid="submit-review-btn">
                  {submittingReview ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Review'}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MarketplacePage;
